import React from "react";
import { useTranslation } from "react-i18next";
import { MeFlightAlertItem } from "../../api/meFlightAlerts.service";

export interface StopConfirmationDialogProps {
  alert: MeFlightAlertItem;
  isStopping: boolean;
  errorMessage: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * "Stop confirmation" (DASH-6346 UI) -- names the flight and date, asks once, says the alert
 * can't be resumed. A modal over the Alert list rather than a separate route: the epic's flow
 * treats it as a single step ("asks to confirm... backs out -> nothing changes"), not a
 * navigable screen of its own.
 */
export const StopConfirmationDialog: React.FC<StopConfirmationDialogProps> = ({
  alert,
  isStopping,
  errorMessage,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation("flightAlerts");

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("stopConfirmation.heading")}
      onClick={onCancel}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex w-full max-w-[400px] flex-col gap-4 rounded-2xl border-2 border-ink-12 bg-card p-6"
      >
        <h2 className="font-lexend text-[20px]/[24px] font-bold tracking-[-0.4px] text-ink-100">
          {t("stopConfirmation.heading")}
        </h2>
        <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
          {t("stopConfirmation.body", {
            flightNumber: alert.operatingDesignator,
            date: alert.flightDate,
          })}
        </p>

        {errorMessage && (
          <p className="font-lexend text-[13px]/[16px] font-normal tracking-normal text-flight-red">
            {errorMessage}
          </p>
        )}

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={isStopping}
            className={`w-full rounded-2xl py-3 text-center font-lexend text-[16px]/[20px] font-bold tracking-normal text-white ${
              isStopping ? "cursor-not-allowed bg-flight-red/60" : "bg-flight-red"
            }`}
          >
            {isStopping ? t("stopConfirmation.confirming") : t("stopConfirmation.confirm")}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isStopping}
            className="w-full rounded-2xl py-3 text-center font-lexend text-[16px]/[20px] font-bold tracking-normal text-ink-60"
          >
            {t("stopConfirmation.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};
